import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Simple LogoCircle component for web
interface LogoCircleProps {
  size?: number;
  backgroundColor?: string;
}

const LogoCircle: React.FC<LogoCircleProps> = ({
  size = 40,
  backgroundColor = "bg-kareva-pink",
}) => (
  <div
    className={`rounded-full ${backgroundColor} flex items-center justify-center p-0.5`}
    style={{ width: size, height: size }}
  >
    <img
      src="/kareva-logo-black.svg"
      alt="Kareva Logo"
      className="w-full h-full object-contain brightness-0 invert"
      style={{ filter: "brightness(0) invert(1)" }}
    />
  </div>
);

// Simple MobileMockup component for web
interface MobileMockupProps {
  screenContent?: string;
}

const MobileMockup: React.FC<MobileMockupProps> = ({ screenContent }) => (
  <div className="w-64 h-[500px] md:w-72 md:h-[600px] bg-gradient-to-r from-gray-300 to-gray-400 rounded-[50px] p-px flex items-center justify-center shadow-2xl relative">
    {/* Left side buttons (Volume up/down) */}
    <div className="absolute -left-0.5 top-[100px] space-y-2">
      <div className="w-0.5 h-10 bg-gray-600 rounded-l-xl" />
      <div className="w-0.5 h-10 bg-gray-600 rounded-l-xl" />
    </div>

    {/* Right side button (Power) */}
    <div className="absolute -right-0.5 top-[120px]">
      <div className="w-0.5 h-16 bg-gray-600 rounded-r-xl" />
    </div>

    {/* Middle frame layer */}
    <div className="w-full h-full bg-gray-700 rounded-[48px] p-1 flex items-center justify-center">
      {/* Inner frame layer */}
      <div className="w-full h-full bg-gray-900 rounded-[46px] p-2 flex items-center justify-center">
        {/* Screen with notch */}
        <div className="w-full h-full bg-white rounded-[40px] flex items-center justify-center text-sm md:text-base text-gray-600 relative overflow-hidden">
          {/* Dynamic Island / Notch */}
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-24 h-7 bg-gray-900 rounded-full" />

          {/* Screen content */}
          <div className="mt-12">
            {screenContent === "nutrition" ? "🥗 Nutrition Screen" : "App Screen"}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Landing: React.FC = () => {
  const { t } = useTranslation();
  const scrollViewRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.title = t("meta.title");
  }, [t]);

  const handleDownload = () => {
    // TODO: App Store URL einfügen
    window.open("https://apps.apple.com/app/kareva", "_blank");
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="font-sans leading-relaxed m-0 p-0" ref={scrollViewRef}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 flex justify-between items-center h-16 md:h-20">
          <nav className="flex items-center gap-4 md:gap-8">
            <div className="flex items-center gap-2">
              <LogoCircle size={32} />
              <div className="text-xl md:text-2xl font-semibold text-gray-900 font-shantell">
                Kareva
              </div>
            </div>
            <div className="hidden md:flex gap-6">
              <button
                type="button"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer bg-transparent border-none"
                onClick={() => scrollToSection("features")}
              >
                {t("navigation.features")}
              </button>
              <button
                type="button"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer bg-transparent border-none"
                onClick={() => scrollToSection("pricing")}
              >
                {t("navigation.pricing")}
              </button>
              <button
                type="button"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer bg-transparent border-none"
                onClick={() => scrollToSection("download")}
              >
                {t("navigation.download")}
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden text-gray-700 hover:text-gray-900 bg-transparent border-none p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <FontAwesomeIcon icon={isMenuOpen ? "xmark" : "bars"} className="text-lg" />
            </button>
          </nav>
          <div className="flex items-center">
            <button
              type="button"
              className="bg-kareva-dark text-white border-none rounded-full px-3 py-1.5 md:px-6 md:py-3 font-bold cursor-pointer text-xs md:text-sm hover:opacity-90 transition-opacity"
              onClick={handleDownload}
            >
              <span className="hidden md:inline">{t("navigation.download")}</span>
              <span className="md:hidden">App</span>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4">
            <div className="flex flex-col space-y-4">
              <button
                type="button"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer bg-transparent border-none text-left py-2"
                onClick={() => {
                  scrollToSection("features");
                  setIsMenuOpen(false);
                }}
              >
                {t("navigation.features")}
              </button>
              <button
                type="button"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer bg-transparent border-none text-left py-2"
                onClick={() => {
                  scrollToSection("pricing");
                  setIsMenuOpen(false);
                }}
              >
                {t("navigation.pricing")}
              </button>
              <button
                type="button"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer bg-transparent border-none text-left py-2"
                onClick={() => {
                  scrollToSection("download");
                  setIsMenuOpen(false);
                }}
              >
                {t("navigation.download")}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="bg-white py-12 md:py-24 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl md:text-6xl font-semibold leading-tight text-gray-900 mb-4 md:mb-6 font-shantell">
              {t("hero.title")} <span className="text-kareva-pink">{t("hero.titleHighlight")}</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 mb-8 md:mb-10 leading-relaxed">
              {t("hero.description")}
            </p>
            <button
              type="button"
              className="bg-kareva-dark text-white border-none rounded-full px-6 py-3 md:px-8 md:py-4 font-bold cursor-pointer text-sm md:text-base hover:opacity-90 transition-opacity"
              onClick={handleDownload}
            >
              {t("hero.downloadApp")}
            </button>
          </div>
          <div className="flex-1 flex justify-center mt-8 md:mt-0">
            <MobileMockup screenContent="nutrition" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-24 px-4" id="features">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-16">
            <p className="text-sm md:text-base font-semibold text-kareva-pink mb-2">
              {t("features.sectionSubtitle")}
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 text-center mb-4 font-shantell">
              {t("features.sectionTitle")}
            </h2>
            <p className="text-base md:text-lg text-gray-600 text-center mb-8 md:mb-16">
              {t("features.sectionDescription")}
            </p>
          </div>

          <div className="space-y-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl md:text-2xl flex-shrink-0 bg-orange-100 mx-auto sm:mx-0">
                  <FontAwesomeIcon icon="utensils" className="text-orange-600" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2 font-shantell">
                    {t("features.personalizedNutrition.title")}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600">
                    {t("features.personalizedNutrition.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl md:text-2xl flex-shrink-0 bg-yellow-50 mx-auto sm:mx-0">
                  <FontAwesomeIcon icon="chart-line" className="text-yellow-600" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2 font-shantell">
                    {t("features.intelligentTracking.title")}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600">
                    {t("features.intelligentTracking.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl md:text-2xl flex-shrink-0 bg-kareva-pink-light mx-auto sm:mx-0">
                  <FontAwesomeIcon icon="users" className="text-kareva-pink" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2 font-shantell">
                    {t("features.community.title")}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600">
                    {t("features.community.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 md:py-24 px-4 bg-gray-50" id="pricing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-16">
            <p className="text-sm md:text-base font-semibold text-kareva-pink mb-2">
              {t("pricing.sectionSubtitle")}
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4 font-shantell">
              {t("pricing.sectionTitle")}
            </h2>
            <p className="text-base md:text-lg text-gray-600 mb-8 md:mb-16">
              {t("pricing.sectionDescription")}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {/* Basic Plan */}
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 relative">
              <h3 className="text-base md:text-lg font-medium text-gray-900 text-center mb-4 font-shantell">
                {t("pricing.basic.title")}
              </h3>
              <div className="text-center">
                <span className="text-3xl md:text-4xl font-semibold text-gray-900">
                  {t("pricing.basic.price")}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 text-center mb-6 md:mb-8">
                {t("pricing.basic.subtitle")}
              </p>

              <div className="mb-8">
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.basic.features.calorieTracking")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.basic.features.waterTracking")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.basic.features.communityAccess")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="times" className="text-gray-400 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-400">
                    {t("pricing.basic.features.personalConsultation")}
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="bg-transparent text-kareva-pink border-2 border-kareva-pink rounded-full px-6 py-3 md:px-8 md:py-4 font-bold cursor-pointer text-sm md:text-base w-full hover:bg-kareva-pink hover:text-white transition-colors"
                onClick={handleDownload}
              >
                {t("pricing.basic.button")}
              </button>
            </div>

            {/* Premium Plan */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 relative">
              <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2 bg-kareva-pink text-white px-3 md:px-4 py-1 rounded-full text-xs md:text-sm font-medium">
                {t("pricing.premium.popular")}
              </div>
              <h3 className="text-base md:text-lg font-medium text-gray-900 text-center mb-4 font-shantell">
                {t("pricing.premium.title")}
              </h3>
              <div className="text-center">
                <span className="text-3xl md:text-4xl font-semibold text-gray-900">
                  {t("pricing.premium.price")}
                </span>
                <span className="text-sm md:text-base font-medium text-gray-500">
                  {t("pricing.premium.period")}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 text-center mb-6 md:mb-8">
                {t("pricing.premium.subtitle")}
              </p>

              <div className="mb-8">
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.premium.features.everythingFromBasic")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.premium.features.personalConsultation")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.premium.features.individualPlan")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.premium.features.progressTracking")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.premium.features.prioritySupport")}
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="bg-kareva-pink text-white border-none rounded-full px-6 py-3 md:px-8 md:py-4 font-bold cursor-pointer text-sm md:text-base w-full hover:opacity-90 transition-opacity"
                onClick={handleDownload}
              >
                {t("pricing.premium.button")}
              </button>
            </div>

            {/* Pro Plan */}
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 relative">
              <h3 className="text-base md:text-lg font-medium text-gray-900 text-center mb-4 font-shantell">
                {t("pricing.pro.title")}
              </h3>
              <div className="text-center">
                <span className="text-3xl md:text-4xl font-semibold text-gray-900">
                  {t("pricing.pro.price")}
                </span>
                <span className="text-sm md:text-base font-medium text-gray-500">
                  {t("pricing.pro.period")}
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 text-center mb-6 md:mb-8">
                {t("pricing.pro.subtitle")}
              </p>

              <div className="mb-8">
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.pro.features.everythingFromPremium")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.pro.features.multipleClients")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.pro.features.advancedAnalytics")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.pro.features.apiAccess")}
                  </span>
                </div>
                <div className="flex items-start gap-3 mb-3 md:mb-4">
                  <FontAwesomeIcon icon="check" className="text-green-500 mt-0.5 text-sm" />
                  <span className="text-xs md:text-sm text-gray-600">
                    {t("pricing.pro.features.whiteLabel")}
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="bg-transparent text-kareva-pink border-2 border-kareva-pink rounded-full px-6 py-3 md:px-8 md:py-4 font-bold cursor-pointer text-sm md:text-base w-full hover:bg-kareva-pink hover:text-white transition-colors"
                onClick={handleDownload}
              >
                {t("pricing.pro.button")}
              </button>
            </div>
          </div>

          <div className="mt-8 md:mt-12 text-center">
            <p className="text-xs md:text-sm text-gray-500">{t("pricing.trialInfo")}</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-kareva-pink py-12 md:py-16 px-4 text-center" id="download">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 font-shantell">
            {t("cta.title")}
          </h2>
          <p className="text-base md:text-lg text-kareva-pink-light mb-6 md:mb-8">
            {t("cta.description")}
          </p>
          <button
            type="button"
            className="bg-white text-kareva-pink border-none rounded-full px-6 py-3 md:px-8 md:py-4 font-bold cursor-pointer text-sm md:text-base shadow-lg hover:shadow-xl transition-shadow"
            onClick={handleDownload}
          >
            {t("cta.downloadApp")}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 md:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between items-center lg:items-start gap-8 mb-8 md:mb-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-4">
                <LogoCircle size={32} />
                <div className="text-left">
                  <div className="text-lg md:text-xl font-bold text-white font-shantell">
                    Kareva
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">{t("footer.tagline")}</div>
                </div>
              </div>

              <div className="flex flex-wrap justify-center lg:justify-start gap-4 md:gap-6 mt-6">
                <button
                  type="button"
                  className="text-xs md:text-sm text-gray-400 hover:text-white cursor-pointer bg-transparent border-none"
                  onClick={() => scrollToSection("features")}
                >
                  {t("footer.navigation.features")}
                </button>
                <button
                  type="button"
                  className="text-xs md:text-sm text-gray-400 hover:text-white cursor-pointer bg-transparent border-none"
                  onClick={() => scrollToSection("pricing")}
                >
                  {t("footer.navigation.pricing")}
                </button>
                <a
                  href="/privacy"
                  className="text-xs md:text-sm text-gray-400 hover:text-white cursor-pointer"
                >
                  {t("footer.navigation.privacy")}
                </a>
                <a
                  href="/imprint"
                  className="text-xs md:text-sm text-gray-400 hover:text-white cursor-pointer"
                >
                  {t("footer.navigation.imprint")}
                </a>
                <a
                  href="/support"
                  className="text-xs md:text-sm text-gray-400 hover:text-white cursor-pointer"
                >
                  {t("footer.navigation.support")}
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-lg flex items-center justify-center text-xs text-gray-600 text-center">
                QR
                <br />
                Code
              </div>
              <div className="max-w-48 text-center lg:text-left">
                <div className="text-base md:text-lg font-semibold text-white mb-2">
                  {t("footer.downloadSection.title")}
                </div>
                <div className="text-xs md:text-sm text-gray-400">
                  {t("footer.downloadSection.description")}
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 md:pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-xs md:text-sm text-gray-400 order-2 md:order-1 text-center md:text-left">
              {t("footer.copyright")}
            </div>
            <div className="flex items-center gap-3 order-1 md:order-2 w-full md:w-auto justify-center md:justify-end">
              <input
                className="bg-gray-800 border border-gray-600 rounded-full px-4 py-2 md:py-3 text-gray-400 text-xs md:text-sm flex-1 md:flex-none md:min-w-48"
                placeholder={t("footer.newsletter.placeholder")}
                type="email"
              />
              <button
                type="button"
                className="bg-kareva-pink text-white border-none rounded-full px-4 py-2 md:px-6 md:py-3 font-bold cursor-pointer text-xs md:text-sm hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                {t("footer.newsletter.button")}
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
